/**
 * ACL-Editor fuer Kundenakten (Phase R4a-5).
 *
 * Wird genutzt in:
 *  - AdminAktenstrukturPage: Default-ACL pro Kategorie
 *  - Kundenakte-Inline-Override: Per-Kunde-ACL auf einzelner Sub-Akte
 *
 * Subjects (Personen + Gruppen) kommen aus
 * `GET /api/modules/bibliothek/acl-subjects`.
 *
 * `allowReset` = true bei Per-Kunde-Override: ein „Auf Default zuruecksetzen"-
 * Button uebergibt (null, null) an onSave, sodass das Backend die Override
 * loescht und die Vererbung wieder greift.
 *
 * Seit 2026-09-30 (Regel „Dokument-Navigation" E6, Abhakliste 1.47): Dialog mit
 * Schutz — Klick daneben, X und Escape fragen nach, sobald Haken geändert sind.
 *
 * Joerg 2026-06-02.
 */
import { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { GuardedModal, isEqualValue } from '@onvice/portal-ui';

interface Subject {
  id: string;
  name: string;
  kind: 'group' | 'person';
  description?: string | null;
}

interface SubjectsResponse {
  success: boolean;
  data: { groups: Subject[]; persons: Subject[] };
}

const BIBLIOTHEK_BASE = '/api/modules/bibliothek';

interface Props {
  open: boolean;
  title: string;
  /** Aktuelle readers. null = vererbt (bei Override) / nichts gesetzt (bei Default). */
  readers: string[] | null;
  authors: string[] | null;
  allowReset?: boolean;
  saving?: boolean;
  onClose: () => void;
  /** null = Reset auf Vererbung. */
  onSave: (readers: string[] | null, authors: string[] | null) => Promise<void> | void;
}

function sorted(list: string[] | null): string[] { return [...(list ?? [])].sort(); }

export default function AktenAclEditModal({ open, title, readers, authors, allowReset, saving, onClose, onSave }: Props) {
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [readerIds, setReaderIds] = useState<string[]>(readers ?? []);
  const [authorIds, setAuthorIds] = useState<string[]>(authors ?? []);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReaderIds(readers ?? []);
    setAuthorIds(authors ?? []);
    setError(null);
  }, [open, readers, authors]);

  useEffect(() => {
    if (!open) return;
    setLoadingSubjects(true);
    setError(null);
    fetch(`${BIBLIOTHEK_BASE}/acl-subjects`, { credentials: 'include' })
      .then((r) => r.json() as Promise<SubjectsResponse>)
      .then((d) => {
        if (d.success) {
          // Gruppen zuerst (in UI bevorzugt), dann Personen.
          setAllSubjects([...d.data.groups, ...d.data.persons]);
        } else {
          setError('Personen und Gruppen konnten nicht geladen werden.');
        }
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoadingSubjects(false));
  }, [open]);

  const dirty = !isEqualValue(sorted(readerIds), sorted(readers)) || !isEqualValue(sorted(authorIds), sorted(authors));

  function toggle(list: string[], setter: (v: string[]) => void, id: string) {
    if (list.includes(id)) setter(list.filter((x) => x !== id));
    else setter([...list, id]);
  }

  async function handleSave(reset = false) {
    setError(null);
    try {
      if (reset) {
        await onSave(null, null);
      } else {
        await onSave(readerIds, authorIds);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <GuardedModal
      open={open}
      title={title}
      label="Zugriffsrechte"
      dirty={dirty}
      busy={!!saving}
      onClose={onClose}
      size="lg"
      testAnchor="akten-acl-dialog"
      footer={(requestClose) => (
        <div className="flex items-center justify-between gap-2 w-full">
          {allowReset ? (
            <button
              type="button"
              onClick={() => void handleSave(true)}
              disabled={saving}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm disabled:opacity-50 min-h-[44px]"
              title="Override entfernen — erbt wieder vom Default"
            >
              <RotateCcw size={14} /> Auf Default zurücksetzen
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button type="button" onClick={requestClose} disabled={saving} className="px-4 py-2 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm min-h-[44px] disabled:opacity-50">
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void handleSave(false)}
              disabled={saving || loadingSubjects}
              className="px-4 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm disabled:opacity-50 inline-flex items-center gap-2 min-h-[44px]"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Speichern
            </button>
          </div>
        </div>
      )}
    >
      <div className="space-y-5">
        {loadingSubjects ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : (
          <>
            <AclColumn
              title="Wer darf lesen (Reader)"
              description="Sieht die Sub-Akte und ihre Dokumente."
              subjects={allSubjects}
              selectedIds={readerIds}
              onToggle={(id) => toggle(readerIds, setReaderIds, id)}
            />
            <AclColumn
              title="Wer darf bearbeiten (Author)"
              description="Darf Dokumente in dieser Sub-Akte hochladen, editieren und löschen."
              subjects={allSubjects}
              selectedIds={authorIds}
              onToggle={(id) => toggle(authorIds, setAuthorIds, id)}
            />
          </>
        )}
        {error && <div className="text-sm text-rose-400" data-test-anchor="akten-acl-fehler">{error}</div>}
      </div>
    </GuardedModal>
  );
}

interface ColumnProps {
  title: string;
  description: string;
  subjects: Subject[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

function AclColumn({ title, description, subjects, selectedIds, onToggle }: ColumnProps) {
  const groups = subjects.filter((s) => s.kind === 'group');
  const persons = subjects.filter((s) => s.kind === 'person');
  const [showPersons, setShowPersons] = useState(false);
  const [personSearch, setPersonSearch] = useState('');

  const filteredPersons = personSearch.trim()
    ? persons.filter((p) => p.name.toLowerCase().includes(personSearch.trim().toLowerCase()))
    : persons.slice(0, 30);

  return (
    <section className="space-y-2">
      <header>
        <h4 className="text-sm font-semibold text-slate-100">{title}</h4>
        <p className="text-xs text-slate-500">{description}</p>
      </header>

      <div className="space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-2">Gruppen</div>
        <div className="flex flex-wrap gap-1.5">
          {groups.map((g) => {
            const on = selectedIds.includes(g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => onToggle(g.id)}
                aria-pressed={on}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  on
                    ? 'bg-brand-600/30 border-brand-600/60 text-brand-200'
                    : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
                title={g.description ?? undefined}
              >
                {g.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1">
        <button
          type="button"
          onClick={() => setShowPersons((v) => !v)}
          className="text-[11px] uppercase tracking-wider text-slate-500 hover:text-slate-300 mt-2"
        >
          Einzelpersonen {showPersons ? '▾' : '▸'} {selectedIds.filter((id) => persons.some((p) => p.id === id)).length > 0 && (
            <span className="ml-1 text-brand-400 normal-case">
              ({selectedIds.filter((id) => persons.some((p) => p.id === id)).length} ausgewählt)
            </span>
          )}
        </button>
        {showPersons && (
          <div className="space-y-1">
            <input
              type="text"
              value={personSearch}
              onChange={(e) => setPersonSearch(e.target.value)}
              placeholder="Person suchen…"
              className="w-full px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100 text-xs"
            />
            <div className="max-h-40 overflow-y-auto flex flex-wrap gap-1.5">
              {filteredPersons.map((p) => {
                const on = selectedIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onToggle(p.id)}
                    aria-pressed={on}
                    className={`text-xs px-2 py-1 rounded border transition-colors ${
                      on
                        ? 'bg-brand-600/30 border-brand-600/60 text-brand-200'
                        : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
            {personSearch.trim() === '' && persons.length > 30 && (
              <div className="text-[10px] text-slate-500">{persons.length - 30} weitere — bitte suchen.</div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
